import React from 'react';
import {
  Flex,
  Box,
  Heading,
  Text,
  DataTable,
  LoadingOrbit,
  Card,
  CardContent,
  Button,
  Select,
  Divider,
  VideoPlayerWithTimeline,
  TagBadge,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import type { VideoTimeline } from '@chatsdet/types';
import { ArrowSquareOut } from '@phosphor-icons/react';
import {
  useOrganizationsQuery,
  useSuperadminBuilderSessionsQuery,
  useSuperadminBuilderSessionDetailQuery,
  useSuperadminBuilderThoughtsQuery,
  useSuperadminBuilderVideoUrlQuery,
  useSuperadminBuilderTraceUrlQuery,
  formatDateTime,
  formatRelative,
  type SuperadminBuilderSession,
  type BuilderSessionAgentRun,
} from './useSuperadminData';
import {
  calculateThoughtTimestamps,
  type ThoughtWithTimestamp,
} from '../FlowDesigner/hooks/useWorkflowRunThoughts';

const SENTRY_BASE_URL =
  'https://tstc-9n.sentry.io/explore/logs/?logsFields=timestamp&logsFields=message&logsSortBys=-timestamp';

const statusVariants: Record<
  string,
  'secondary' | 'warning' | 'success' | 'destructive' | 'outline' | 'default'
> = {
  pending: 'secondary',
  exploring: 'warning',
  validating: 'warning',
  completed: 'success',
  failed: 'destructive',
  cancelled: 'outline',
};

const statusLabels: Record<string, string> = {
  pending: 'Pending',
  exploring: 'Exploring',
  validating: 'Validating',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const statusOptions = [
  { id: '', label: 'All Statuses' },
  { id: 'pending', label: 'Pending' },
  { id: 'exploring', label: 'Exploring' },
  { id: 'validating', label: 'Validating' },
  { id: 'completed', label: 'Completed' },
  { id: 'failed', label: 'Failed' },
  { id: 'cancelled', label: 'Cancelled' },
];

function formatDuration(start: string, end: string | null): string {
  if (!end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 0) return '—';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

const columns: ColumnDef<StockFeatures, SuperadminBuilderSession>[] = [
  {
    accessorKey: 'orgName',
    header: 'Org',
    cell: ({ row }) => (
      <Text
        size="sm"
        style={{
          maxWidth: 120,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {row.original.orgName}
      </Text>
    ),
  },
  {
    accessorKey: 'workflowName',
    header: 'Workflow',
    cell: ({ row }) => (
      <Text
        size="sm"
        style={{
          maxWidth: 150,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {row.original.workflowName}
      </Text>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <TagBadge
        name={statusLabels[row.original.status] ?? row.original.status}
        color={badgeVariantToTagColor(statusVariants[row.original.status] ?? 'secondary')}
        hideDot
      />
    ),
  },
  {
    accessorKey: 'nodeCount',
    header: 'Nodes',
    cell: ({ row }) => <Text size="sm">{row.original.nodeCount}</Text>,
  },
  {
    accessorKey: 'goal',
    header: 'Goal',
    cell: ({ row }) => (
      <Text
        size="sm"
        style={{
          maxWidth: 250,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {row.original.goal}
      </Text>
    ),
  },
  {
    accessorKey: 'agentRunCount',
    header: 'Runs',
    cell: ({ row }) => <Text size="sm">{row.original.agentRunCount}</Text>,
  },
  {
    id: 'duration',
    header: 'Duration',
    cell: ({ row }) => (
      <Text size="sm">{formatDuration(row.original.createdAt, row.original.completedAt)}</Text>
    ),
  },
  {
    accessorKey: 'createdAt',
    header: 'Created',
    cell: ({ row }) => (
      <Text size="sm" title={formatDateTime(row.original.createdAt)}>
        {formatRelative(row.original.createdAt)}
      </Text>
    ),
  },
];

function BuilderSessionDetail({ sessionId }: { sessionId: string }) {
  const [showThoughts, setShowThoughts] = React.useState(true);
  const [expandedVideo, setExpandedVideo] = React.useState(false);

  const detailQuery = useSuperadminBuilderSessionDetailQuery(sessionId);
  const thoughtsQuery = useSuperadminBuilderThoughtsQuery(sessionId);
  const videoUrlQuery = useSuperadminBuilderVideoUrlQuery(sessionId);
  const traceUrlQuery = useSuperadminBuilderTraceUrlQuery(sessionId);

  if (detailQuery.isLoading) return <LoadingOrbit />;
  if (detailQuery.isError) return <Text color="destructive">Failed to load detail</Text>;

  const detail = detailQuery.data?.data;
  if (!detail) return <Text color="destructive">Not found</Text>;

  const { job, agentRuns, validationRun } = detail;

  // Calculate thought timestamps for video alignment
  const timeline = job.videoTimelineJson as VideoTimeline | null;
  const thoughts = thoughtsQuery.data ?? [];
  const thoughtsWithTimestamps: ThoughtWithTimestamp[] = React.useMemo(() => {
    if (thoughts.length === 0) return [];
    // Map builder thoughts to the shape expected by calculateThoughtTimestamps
    const mappedThoughts = thoughts.map((t) => ({
      id: t.id,
      runId: t.builderJobId,
      stepId: null,
      nodeId: null,
      content: t.content,
      toolName: t.toolName,
      orderIndex: t.orderIndex,
      startedAt: null,
      createdAt: t.createdAt,
    }));
    return calculateThoughtTimestamps(mappedThoughts, timeline);
  }, [thoughts, timeline]);

  const metadata = job.metadataJson as Record<string, unknown> | null;
  const sentryUrl = `${SENTRY_BASE_URL}&logsQuery=annotations.job.id:${job.jobId ?? sessionId}`;

  return (
    <Card>
      <CardContent>
        <Flex direction="column" gap="md">
          {/* Metadata */}
          <Flex gap="lg" wrap="wrap">
            <Flex direction="column" gap="xs">
              <Text size="xs" weight="medium" color="muted">
                Status
              </Text>
              <TagBadge
                name={statusLabels[job.status] ?? job.status}
                color={badgeVariantToTagColor(statusVariants[job.status] ?? 'secondary')}
                hideDot
              />
            </Flex>
            <Flex direction="column" gap="xs">
              <Text size="xs" weight="medium" color="muted">
                Nodes
              </Text>
              <Text size="sm">{job.nodeCount}</Text>
            </Flex>
            <Flex direction="column" gap="xs">
              <Text size="xs" weight="medium" color="muted">
                Duration
              </Text>
              <Text size="sm">{formatDuration(job.createdAt, job.completedAt)}</Text>
            </Flex>
            <Flex direction="column" gap="xs">
              <Text size="xs" weight="medium" color="muted">
                Created
              </Text>
              <Text size="sm">{formatDateTime(job.createdAt)}</Text>
            </Flex>
            {Boolean(metadata?.retryTriggered) && (
              <TagBadge name="Retry triggered" color="yellow" hideDot />
            )}
          </Flex>

          <Flex direction="column" gap="xs">
            <Text size="xs" weight="medium" color="muted">
              Goal
            </Text>
            <Text size="sm">{job.goal}</Text>
          </Flex>

          {job.errorMessage && (
            <Flex direction="column" gap="xs">
              <Text size="xs" weight="medium" color="destructive">
                Error
              </Text>
              <Text size="sm" color="destructive">
                {job.errorMessage}
              </Text>
            </Flex>
          )}

          <Divider />

          {/* Video Player */}
          {job.videoStorageKey ? (
            <Flex direction="column" gap="sm">
              <Heading level={4}>Builder Exploration Video</Heading>
              <VideoPlayerWithTimeline
                posterSrc={null}
                videoSrc={videoUrlQuery.data ?? null}
                timeline={timeline ?? null}
                thoughts={thoughtsWithTimestamps}
                showThoughts={showThoughts}
                onThoughtsVisibilityChange={setShowThoughts}
                isFullscreen={expandedVideo}
                onFullscreenChange={setExpandedVideo}
              />
            </Flex>
          ) : (
            <Flex direction="column" gap="xs" align="center" style={{ padding: '24px 0' }}>
              <Text size="sm" color="muted">
                No video recording available
              </Text>
            </Flex>
          )}

          <Divider />

          {/* Agent Runs */}
          <Flex direction="column" gap="sm">
            <Heading level={4}>Agent Runs ({agentRuns.length})</Heading>
            {agentRuns.length > 0 ? (
              <AgentRunsTable runs={agentRuns} />
            ) : (
              <Text size="sm" color="muted">
                No agent runs recorded
              </Text>
            )}
          </Flex>

          {/* Validation Run */}
          {validationRun && (
            <>
              <Divider />
              <Flex direction="column" gap="sm">
                <Heading level={4}>Validation Run</Heading>
                <Flex gap="md">
                  <TagBadge
                    name={validationRun.status}
                    color={badgeVariantToTagColor(
                      validationRun.status === 'success' ? 'success' : 'destructive'
                    )}
                    hideDot
                  />
                  {validationRun.errorMessage && (
                    <Text size="sm" color="destructive">
                      {validationRun.errorMessage}
                    </Text>
                  )}
                </Flex>
              </Flex>
            </>
          )}

          <Divider />

          {/* External Links */}
          <Flex gap="sm" wrap="wrap">
            {traceUrlQuery.data && (
              <Button
                variant="outline"
                size="sm"
                onPress={() => window.open(traceUrlQuery.data!, '_blank')}
              >
                <ArrowSquareOut size={14} />
                Trace Viewer
              </Button>
            )}
            <Button variant="outline" size="sm" onPress={() => window.open(sentryUrl, '_blank')}>
              <ArrowSquareOut size={14} />
              Sentry Logs
            </Button>
            <Button
              variant="outline"
              size="sm"
              onPress={() =>
                window.open(`/superadmin/workflows?workflowId=${job.workflowId}`, '_blank')
              }
            >
              <ArrowSquareOut size={14} />
              Workflow
            </Button>
          </Flex>
        </Flex>
      </CardContent>
    </Card>
  );
}

const agentRunColumns: ColumnDef<StockFeatures, BuilderSessionAgentRun>[] = [
  {
    accessorKey: 'modelId',
    header: 'Model',
    cell: ({ row }) => <Text size="sm">{row.original.modelId ?? '—'}</Text>,
  },
  {
    accessorKey: 'totalSteps',
    header: 'Steps',
    cell: ({ row }) => <Text size="sm">{row.original.totalSteps}</Text>,
  },
  {
    accessorKey: 'tokensTotal',
    header: 'Tokens',
    cell: ({ row }) => (
      <Text size="sm">
        {row.original.tokensTotal !== null && row.original.tokensTotal !== undefined
          ? row.original.tokensTotal.toLocaleString()
          : '—'}
      </Text>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <TagBadge
        name={row.original.status}
        color={badgeVariantToTagColor(
          row.original.status === 'succeeded'
            ? 'success'
            : row.original.status === 'failed'
              ? 'destructive'
              : 'warning'
        )}
        hideDot
      />
    ),
  },
  {
    id: 'duration',
    header: 'Duration',
    cell: ({ row }) => (
      <Text size="sm">{formatDuration(row.original.startedAt, row.original.completedAt)}</Text>
    ),
  },
];

function AgentRunsTable({ runs }: { runs: BuilderSessionAgentRun[] }) {
  return <DataTable columns={agentRunColumns} data={runs} />;
}

export function SuperadminBuilderSessions() {
  const [filterOrgId, setFilterOrgId] = React.useState<string>('');
  const [filterStatus, setFilterStatus] = React.useState<string>('');
  const [page, setPage] = React.useState(1);
  const [selectedSessionId, setSelectedSessionId] = React.useState<string | null>(null);

  const organizationsQuery = useOrganizationsQuery();
  const sessionsQuery = useSuperadminBuilderSessionsQuery({
    orgId: filterOrgId || undefined,
    status: filterStatus || undefined,
    page,
    pageSize: 25,
  });

  const orgOptions = React.useMemo(() => {
    const orgs = organizationsQuery.data?.organizations ?? [];
    return [
      { id: '', label: 'All Organizations' },
      ...orgs.map((org) => ({ id: org.id, label: org.name })),
    ];
  }, [organizationsQuery.data]);

  const sessions = sessionsQuery.data?.data ?? [];
  const pagination = sessionsQuery.data?.pagination;

  return (
    <Flex direction="column" gap="md">
      {/* Filters */}
      <Card>
        <CardContent>
          <Flex gap="sm" align="end">
            <Box style={{ minWidth: 200 }}>
              <Select
                label="Organization"
                items={orgOptions}
                selectedKey={filterOrgId}
                onSelectionChange={(key) => {
                  setFilterOrgId(key as string);
                  setPage(1);
                }}
              />
            </Box>
            <Box style={{ minWidth: 160 }}>
              <Select
                label="Status"
                items={statusOptions}
                selectedKey={filterStatus}
                onSelectionChange={(key) => {
                  setFilterStatus(key as string);
                  setPage(1);
                }}
              />
            </Box>
          </Flex>
        </CardContent>
      </Card>

      {/* Table */}
      {sessionsQuery.isLoading ? (
        <LoadingOrbit />
      ) : sessionsQuery.isError ? (
        <Text color="destructive">Failed to load builder sessions</Text>
      ) : (
        <DataTable
          columns={columns}
          data={sessions}
          onRowClick={(row) => setSelectedSessionId(selectedSessionId === row.id ? null : row.id)}
          rowClassName={(row) =>
            row.original.id === selectedSessionId ? 'bg-accent/5' : undefined
          }
          serverPagination={
            pagination
              ? {
                  page: pagination.page,
                  pageSize: pagination.pageSize,
                  totalItems: pagination.totalItems,
                  pageCount: pagination.totalPages,
                  onPageChange: setPage,
                }
              : undefined
          }
        />
      )}

      {/* Detail Panel */}
      {selectedSessionId && <BuilderSessionDetail sessionId={selectedSessionId} />}
    </Flex>
  );
}
